import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  maintenanceService,
  CreateMaintenanceRequest,
  UpdateMaintenanceRequest,
} from '../../../services/maintenanceService';
import { propertyService, PropertyResponseDto, PropertyGroupResponseDto } from '../../../services/propertyService';

const MaintenanceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id || searchParams.get('edit') === 'true';
  const recordId = id ? parseInt(id, 10) : null;
  const contextPropertyIdParam = searchParams.get('propertyId');
  const contextReturnPropertyId = (() => {
    if (!contextPropertyIdParam) return null;
    const n = parseInt(contextPropertyIdParam, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const exitFormNavigate = () => {
    if (contextReturnPropertyId != null) {
      navigate(`/Property Hub/Property/${contextReturnPropertyId}`);
      return;
    }
    navigate('/Property Hub/Maintenance');
  };

  const detailPathAfterSave = (savedId: number) =>
    contextReturnPropertyId != null
      ? `/Property Hub/Maintenance/${savedId}?propertyId=${contextReturnPropertyId}`
      : `/Property Hub/Maintenance/${savedId}`;

  const [groups, setGroups] = useState<PropertyGroupResponseDto[]>([]);
  const [properties, setProperties] = useState<PropertyResponseDto[]>([]);
  const [types, setTypes] = useState<{ maintenanceTypeId: number; maintenanceTypeName: string }[]>([]);
  const [statuses, setStatuses] = useState<{ maintenanceStatusId: number; maintenanceStatusName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<CreateMaintenanceRequest>({
    propertyGroupId: 0,
    propertyId: undefined,
    maintenanceTypeId: 0,
    maintenanceStatusId: undefined,
    summary: '',
    detailNotes: '',
    workDate: undefined,
    addToCalendar: false,
    calendarDate: undefined,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [g, p, t, s] = await Promise.all([
        propertyService.getPropertyGroups(),
        propertyService.getProperties(),
        maintenanceService.getMaintenanceTypes(),
        maintenanceService.getMaintenanceStatuses(),
      ]);
      setGroups(g);
      setProperties(p);
      setTypes(t);
      setStatuses(s.filter((x) => x.isActive !== false));

      if (isEdit && recordId) {
        const r = await maintenanceService.getMaintenanceRecord(recordId);
        setFormData({
          propertyGroupId: r.propertyGroupId,
          propertyId: r.propertyId,
          maintenanceTypeId: r.maintenanceTypeId,
          maintenanceStatusId: r.maintenanceStatusId,
          summary: r.summary || '',
          detailNotes: r.detailNotes || '',
          workDate: r.workDate ? new Date(r.workDate).toISOString().split('T')[0] : undefined,
          addToCalendar: !!r.hasCalendarAppointment,
          calendarDate: r.calendarDate ? new Date(r.calendarDate).toISOString().split('T')[0] : undefined,
        });
      } else {
        const ctxId = contextPropertyIdParam ? parseInt(contextPropertyIdParam, 10) : NaN;
        const match = Number.isFinite(ctxId) ? p.find((x) => x.propertyId === ctxId) : undefined;
        if (match) {
          setFormData((prev) => ({
            ...prev,
            propertyGroupId: match.propertyGroupId,
            propertyId: match.propertyId,
          }));
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isEdit, recordId, contextPropertyIdParam]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const propertiesForGroup = formData.propertyGroupId
    ? properties.filter((p) => p.propertyGroupId === formData.propertyGroupId)
    : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.propertyGroupId || !formData.maintenanceTypeId) {
      setError('Property group and maintenance type are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (recordId) {
        const upd: UpdateMaintenanceRequest = {
          propertyGroupId: formData.propertyGroupId,
          // Send 0 to explicitly clear property on edit (group-level maintenance).
          propertyId: formData.propertyId ?? 0,
          maintenanceTypeId: formData.maintenanceTypeId,
          maintenanceStatusId: formData.maintenanceStatusId,
          summary: formData.summary,
          detailNotes: formData.detailNotes,
          workDate: formData.workDate || undefined,
          addToCalendar: !!formData.addToCalendar,
          calendarDate: formData.addToCalendar ? (formData.calendarDate || formData.workDate || undefined) : undefined,
        };
        await maintenanceService.updateMaintenanceRecord(recordId, upd);
        navigate(detailPathAfterSave(recordId));
      } else {
        const created = await maintenanceService.createMaintenanceRecord({
          ...formData,
          workDate: formData.workDate || undefined,
          addToCalendar: !!formData.addToCalendar,
          calendarDate: formData.addToCalendar ? (formData.calendarDate || formData.workDate || undefined) : undefined,
        });
        navigate(detailPathAfterSave(created.maintenanceId));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{recordId ? 'Edit maintenance' : 'New maintenance'}</h2>
        <button
          type="button"
          onClick={exitFormNavigate}
          className="text-gray-600 hover:text-gray-900"
        >
          {contextReturnPropertyId != null ? 'Property overview' : 'Back to list'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="rounded-lg bg-white p-6 shadow">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Property group *</label>
            <select
              required
              value={formData.propertyGroupId || ''}
              onChange={(e) => {
                const gid = parseInt(e.target.value, 10);
                setFormData({
                  ...formData,
                  propertyGroupId: gid,
                  propertyId: undefined,
                });
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="">Select…</option>
              {groups.map((g) => (
                <option key={g.propertyGroupId} value={g.propertyGroupId}>
                  {g.propertyGroupName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Property</label>
            <select
              value={formData.propertyId ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  propertyId: e.target.value ? parseInt(e.target.value, 10) : undefined,
                })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="">Group-level (no specific property)</option>
              {propertiesForGroup.map((p) => (
                <option key={p.propertyId} value={p.propertyId}>
                  {p.propertyName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Maintenance type *</label>
            <select
              required
              value={formData.maintenanceTypeId || ''}
              onChange={(e) =>
                setFormData({ ...formData, maintenanceTypeId: parseInt(e.target.value, 10) })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="">Select…</option>
              {types.map((t) => (
                <option key={t.maintenanceTypeId} value={t.maintenanceTypeId}>
                  {t.maintenanceTypeName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={formData.maintenanceStatusId ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maintenanceStatusId: e.target.value ? parseInt(e.target.value, 10) : undefined,
                })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="">— None —</option>
              {statuses.map((s) => (
                <option key={s.maintenanceStatusId} value={s.maintenanceStatusId}>
                  {s.maintenanceStatusName}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Summary</label>
            <input
              type="text"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Details</label>
            <textarea
              rows={5}
              value={formData.detailNotes}
              onChange={(e) => setFormData({ ...formData, detailNotes: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Work date</label>
            <input
              type="date"
              value={formData.workDate || ''}
              onChange={(e) => setFormData({ ...formData, workDate: e.target.value || undefined })}
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>
          <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={!!formData.addToCalendar}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    addToCalendar: e.target.checked,
                    calendarDate: e.target.checked ? prev.calendarDate || prev.workDate : undefined,
                  }))
                }
              />
              Add to calendar
            </label>
            {!!formData.addToCalendar && (
              <div className="mt-3">
                <label className="mb-1 block text-sm font-medium text-gray-700">Calendar date</label>
                <input
                  type="date"
                  value={formData.calendarDate || ''}
                  onChange={(e) => setFormData({ ...formData, calendarDate: e.target.value || undefined })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 md:max-w-xs"
                  required
                />
                {recordId && (
                  <p className="mt-2 text-xs text-slate-600">
                    This maintenance item has a linked calendar appointment; saving updates that appointment.
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex items-end gap-2 pb-1 md:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={exitFormNavigate}
              className="rounded-md border border-gray-300 px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MaintenanceForm;
